import React, { useState, useRef, useCallback } from 'react';
import { arrayOf, bool, func, node, number, object, string } from 'prop-types';

import { parseDateFromISO8601, stringifyDateToISO8601 } from '../../../util/dates';
import { FieldDateRangeController } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import FilterPopupForSidebar from './FilterPopupForSidebar';
import css from './BookingDateRangeFilter.module.css';

const getDatesQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'dates';
};

// Parse query parameter, which should look like "2020-05-28,2020-05-31"
const parseValue = value => {
  const rawValuesFromParams = value ? value.split(',') : [];
  const [startDate, endDate] = rawValuesFromParams.map(v => parseDateFromISO8601(v));
  return value && startDate && endDate ? { dates: { startDate, endDate } } : { dates: null };
};
// Format dateRange value for the query. It's given by FieldDateRangeInput:
// { dates: { startDate, endDate } }
const formatValue = (dateRange, queryParamName) => {
  const hasDates = dateRange && dateRange.dates;
  const { startDate, endDate } = hasDates ? dateRange.dates : {};
  const start = startDate ? stringifyDateToISO8601(startDate) : null;
  const end = endDate ? stringifyDateToISO8601(endDate) : null;
  const value = start && end ? `${start},${end}` : null;
  return { [queryParamName]: value };
};

function BookingDateRangeFilterComponent(props) {
  const [_isOpen, setIsOpen] = useState(true);
  const popupControllerRef = useRef(null);
  const plainControllerRef = useRef(null);

  const _toggleIsOpen = useCallback(() => setIsOpen(prev => !prev), []);

  const {
    className = null,
    rootClassName = null,
    showAsPopup = true,
    isDesktop,
    initialValues = null,
    id,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    label,
    minimumNights = 0,
    ...rest
  } = props;

  const datesQueryParamName = getDatesQueryParamName(queryParamNames);
  const initialDates =
    initialValues && initialValues[datesQueryParamName]
      ? parseValue(initialValues[datesQueryParamName])
      : { dates: null };

  const isSelected = !!initialDates.dates;
  const startDate = isSelected ? initialDates.dates.startDate : null;
  const endDate = isSelected ? initialDates.dates.endDate : null;

  // Inline i18n: format dates as 'MMM D'
  const formatDate = date =>
    date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
  const formattedStartDate = isSelected ? formatDate(startDate) : null;
  const formattedEndDate = isSelected ? formatDate(endDate) : null;

  // Inline i18n: label text
  const _labelForPlain = isSelected
    ? `${formattedStartDate} - ${formattedEndDate}`
    : label
    ? label
    : 'Dates';

  const labelForPopup = isSelected
    ? `${formattedStartDate} - ${formattedEndDate}`
    : label
    ? label
    : 'Select dates';

  const labelSelection = isSelected ? `${formattedStartDate} - ${formattedEndDate}` : null;

  const handleSubmit = values => {
    onSubmit(formatValue(values, datesQueryParamName));
  };

  const onClearPopupMaybe =
    popupControllerRef.current && popupControllerRef.current.onReset
      ? { onClear: () => popupControllerRef.current.onReset(null, null) }
      : {};

  const onCancelPopupMaybe =
    popupControllerRef.current && popupControllerRef.current.onReset
      ? { onCancel: () => popupControllerRef.current.onReset(startDate, endDate) }
      : {};

  const onClearPlainMaybe =
    plainControllerRef.current && plainControllerRef.current.onReset
      ? { onClear: () => plainControllerRef.current.onReset(null, null) }
      : {};

  if (showAsPopup) {
    return (
      <FilterPopup
        className={className}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        label={labelForPopup}
        isSelected={isSelected}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPopupMaybe}
        {...onCancelPopupMaybe}
        initialValues={initialDates}
        {...rest}
      >
        <FieldDateRangeController
          name="dates"
          minimumNights={minimumNights}
          controllerRef={node => {
            popupControllerRef.current = node;
          }}
        />
      </FilterPopup>
    );
  } else if (isDesktop) {
    return (
      <FilterPopupForSidebar
        className={className}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        label={label}
        labelSelection={labelSelection}
        isSelected={isSelected}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPopupMaybe}
        {...onCancelPopupMaybe}
        initialValues={initialDates}
        {...rest}
      >
        <FieldDateRangeController
          name="dates"
          minimumNights={minimumNights}
          controllerRef={node => {
            popupControllerRef.current = node;
          }}
        />
      </FilterPopupForSidebar>
    );
  } else {
    return (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={label}
        labelSelection={labelSelection}
        labelSelectionSeparator=":"
        isSelected={isSelected}
        id={`${id}.plain`}
        liveEdit
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPlainMaybe}
        initialValues={initialDates}
        {...rest}
      >
        <FieldDateRangeController
          name="dates"
          minimumNights={minimumNights}
          controllerRef={node => {
            plainControllerRef.current = node;
          }}
        />
      </FilterPlain>
    );
  }
}



BookingDateRangeFilterComponent.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  label: node,
  showAsPopup: bool,
  liveEdit: bool,
  queryParamNames: arrayOf(string).isRequired,
  onSubmit: func.isRequired,
  minimumNights: number,
  initialValues: object,
  contentPlacementOffset: number,
};

export default BookingDateRangeFilterComponent;
